<script setup>
import { ref } from 'vue';

/**
 * Renders large lists efficiently by only mounting visible DOM nodes.
 * This is mostly a pass-through to Quasar. Please see Quasar's documentation for details
 *
 * @status recommended
 * @group lists-tables
 * @useWhen Rendering lists with hundreds or thousands of rows without performance degradation
 * @see https://quasar.dev/vue-components/virtual-scroll Quasar q-virtual-scroll API
 */
defineOptions({ name: 'HrVirtualScroll' });

const props = defineProps({
  /**
   * The array of data items to render. One default-slot call is made per visible item.
   */
  items: {
    type: Array,
    default: () => [],
  },
});

const inner = ref(null);

defineExpose({
  /** Refreshes the virtual scroll's computed properties. */
  refresh: (...args) => inner.value?.refresh(...args),
  /** Resets the virtual scroll to its initial state. */
  reset: (...args) => inner.value?.reset(...args),
  /** Scrolls to the item at the given index. */
  scrollTo: (...args) => inner.value?.scrollTo(...args),
});
</script>

<template>
  <q-virtual-scroll
    ref="inner"
    :items="props.items"
    v-bind="$attrs"
  >
    <template #default="slotProps">
      <!-- @slot default Item renderer; receives Quasar's virtual scroll slot props `{ item, index, scope }`. -->
      <slot v-bind="slotProps" />
    </template>
  </q-virtual-scroll>
</template>
